<template>
    <div>
        <div class="flex pt-4 items-center flex-col justify-center">
            <div
                v-if="questionImage.length"
                :style="{ 'background-image': 'url(' + questionImage + ')' }"
                :class="['border-style bg-cover bg-center bg-norepeat', imageSize[questionPreviewSize]]"
            ></div>
            <h1
                class="theme-question-text-color theme-question-shadow text-white uppercase text-center font-size-30 my-4"
            >
                {{ title }}
            </h1>
        </div>
        <div v-if="userType == 'master' || showSummary">
            <div v-if="getChartType() == 'pie'" id="poll-chart">
                <apexcharts
                    v-if="series.length == 0"
                    type="pie"
                    :options="chartOptionsEmpty"
                    :series="seriesEmpty"
                ></apexcharts>
                <apexcharts id="chart" type="pie" :options="chartOptions" :series="series"></apexcharts>
            </div>
            <div v-else id="original-chart">
                <div class="relative" :key="result.id" v-for="(result, index) in results">
                    <div
                        class="poll-bar w-auto h-16"
                        :class="{ 'mb-2': results.length !== index + 1 }"
                        :style="{
                            width: !result.score ? 0 : result.score + '%',
                            left: 0,
                            'background-color': 'var(--ui-color)'
                        }"
                    ></div>
                    <div
                        style="position: absolute; top: 0"
                        :class="{ 'flex font-size-22 font-bold uppercase block': true, 'text-white': true }"
                    >
                        <div
                            v-if="imageAnswer"
                            :style="{ 'background-image': 'url(' + result.image + ')' }"
                            class="bg-cover bg-center bg-norepeat w-16 h-16 border-solid border-2 border-white rounded-full bg-black ml-4"
                        ></div>
                        <div class="theme-question-text-color theme-question-shadow h-16 flex items-center ml-2">
                            {{ result.title }}
                        </div>
                        <div
                            v-if="result.score"
                            class="theme-question-text-color theme-question-shadow h-16 flex items-center ml-4"
                        >
                            {{ `(${result.score.toFixed(1)}%)` }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { mapState } from 'vuex'

export default {
    name: 'PollResult',

    components: {
        apexcharts: VueApexCharts
    },

    props: {
        userType: String,
        title: String,
        questionImage: String,
        questionPreviewSize: {
            type: String,
            default: 'large'
        },
        answers: Array,
        imageAnswer: Boolean,
        showSummary: Boolean
    },

    data: function () {
        return {
            results: this.answers,
            imageSize: {
                small: 'xs:w-full w-32 h-32 md:w-32 md:h-32',
                large: 'xs:w-full w-48 h-48 md:w-48 md:h-48'
            },

            colorsImage: ['#2B908F', '#F9A3A4', '#90EE7E', '#FA4443', '#69D2E7'],

            //setting for empty chart
            seriesEmpty: [100],
            chartOptionsEmpty: {
                dataLabels: {
                    enabled: false
                },
                tooltip: {
                    enabled: false
                },
                legend: {
                    show: false
                },
                stroke: {
                    show: false
                },
                fill: {
                    colors: ['#fff'],
                    opacity: 0.5
                },
                chart: {
                    width: '100%',
                    type: 'pie',
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        dynamicAnimation: {
                            speed: 500
                        }
                    }
                }
            },

            series: [],
            chartOptions: {
                theme: {
                    palette: 'palette5'
                },
                stroke: {
                    show: false
                },
                chart: {
                    width: '100%',
                    type: 'pie',
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        dynamicAnimation: {
                            speed: 500
                        }
                    }
                },
                legend: {
                    show: false
                },
                labels: this.answers.map(data => {
                    return data.title
                }),
                tooltip: {
                    y: {
                        formatter: function (value) {
                            return value.toFixed(1) + '%'
                        },

                        title: {
                            formatter: function (val) {
                                return val.length > 18 ? val.slice(0, 18) + '...' : val
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    dropShadow: {
                        enabled: false
                    },
                    style: {
                        fontSize: '18px'
                    },
                    background: {
                        enabled: true,
                        foreColor: '#000',
                        padding: 5,
                        borderRadius: 4,
                        borderWidth: 1,
                        borderColor: '#fff',
                        opacity: 0.8
                    },
                    formatter(val, opts) {
                        let name = opts.w.globals.labels[opts.seriesIndex]
                        return [name.length < 10 ? name : name.slice(0, 10) + '...'] + ` (${val.toFixed(1)}%)`
                    }
                },
                responsive: [
                    {
                        breakpoint: 1000,
                        options: {
                            chart: {
                                width: '100%'
                            },
                            legend: {
                                show: false
                            }
                        }
                    }
                ]
            }
        }
    },

    computed: {
        ...mapState({
            questions: state => state.question.questions,
            playingCurrentQuestionIndex: state => state.playingCurrentQuestionIndex
        })
    },

    watch: {
        results: function (value) {
            this.$emit('update:answers', value)
        }
    },

    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('componentAlready'))
            }, 200)
        })
    },

    methods: {
        getChartType() {
            return this.questions[this.playingCurrentQuestionIndex].chart_type
        },

        updateChart() {
            // update series
            this.series = this.answers
                .filter(data => {
                    return !isNaN(data.score)
                })
                .map(item => {
                    return item.score
                })

            //check answers has image
            let isHasImage = this.answers.filter(x => {
                return x.image != ''
            })

            if (isHasImage.length) {
                let colorIndex = 0

                // update series
                this.chartOptions = {
                    fill: {
                        type: 'image',
                        opacity: 0.85,
                        image: {
                            src: this.answers.map(data => {
                                if (data.image != '') {
                                    return data.image
                                } else {
                                    let imgColor = this.colorToPng(this.colorsImage[colorIndex])
                                    colorIndex++
                                    return imgColor
                                }
                            }),
                            width: 25,
                            imagedHeight: 25
                        }
                    }
                }
            }
        },

        colorToPng(color) {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            const width = 100
            const height = 100
            canvas.width = width
            canvas.height = height
            ctx.fillStyle = color
            ctx.fillRect(0, 0, width, height)
            canvas.remove()
            return canvas.toDataURL()
        }
    }
}
</script>
<style scoped>
#poll-chart {
    max-width: 500px;
    margin: 40px auto;
}
</style>